import React from "react";
import { Formik, FieldArray } from "formik";
import { CForm, CRow, CCol, CFormLabel, CButton } from "@coreui/react";
import CustomInput from "@/components/CustomInput";
import { smsTemplate } from "@/constants/translation/smsTemplate.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { useNavigate } from "react-router-dom";
import { masters } from "@/libs/routes";
import CustomEditor from "@/components/CKEditor";
import constants from "@/libs/constant";

/**
 * SmsTemplateForm Component
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {boolean} props.smsTemplateLoaded - Loading state for SMS template
 * @param {Object} [props.validationSchema] - Form validation schema
 * @returns {JSX.Element} React component for SMS template form
 */
const SmsTemplateForm = ({ initialValues, onSubmit, smsTemplateLoaded, validationSchema = {} }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  /**
   * Redirects to the SMS template list page.
   */
  const handleRedirectList = () => {
    navigate(masters.smsTemplate.list);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        
        
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        touched,
        setFieldValue,
        isValid
      }) => (
        <CForm onSubmit={(e) => {
          
          
          e.preventDefault();
          handleSubmit(e);
        }}>
          <CRow className="mb-2 m-auto p-3 border rounded">
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(smsTemplate.form.slug_label)}
                type={constants.inputTypes.text}
                name={constants.formFields.smsTemplate.slug}
                placeholder={t(smsTemplate.form.slug_placeholder)}
                value={values.slug}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={initialValues?.id}
                error={touched.slug && errors.slug}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(smsTemplate.form.parameter_label)}
                type={constants.inputTypes.text}
                name={constants.formFields.smsTemplate.parameter}
                placeholder={t(smsTemplate.form.parameter_label_placeholder)}
                value={values.parameter}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.parameter && errors.parameter}
                disabled={true}
              />
            </CCol>
          </CRow>
          <FieldArray name="sms_templates">
            {() =>
              values.sms_templates.map((lang, index) => (
                <CRow key={index} className="mb-2 m-auto p-3 border rounded">
                  <h5>{lang.language}</h5>
                  <CCol lg={12}>
                    <CustomInput
                      label={t(smsTemplate.form.title_label)}
                      type={constants.inputTypes.text}
                      name={`${constants.formFields.smsTemplate.smsTemplates}.${index}.${constants.formFields.smsTemplate.title}`}
                      placeholder={t(smsTemplate.form.title_placeholder)}
                      value={values.sms_templates[index].title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.sms_templates?.[index]?.title && errors.sms_templates?.[index]?.title}
                    />
                  </CCol>
                  <CCol lg={12}>
                    <CustomInput
                      label={t(smsTemplate.form.content_label)}
                      type="textarea"
                      name={`${constants.formFields.smsTemplate.smsTemplates}.${index}.${constants.formFields.smsTemplate.content}`}
                      placeholder={t(smsTemplate.form.content_placeholder)}
                      value={values.sms_templates[index].content}
                      onChange={(e) => {
                        const content = e.target.value;
                        setFieldValue(`${constants.formFields.smsTemplate.smsTemplates}.${index}.${constants.formFields.smsTemplate.content}`, content);
                        // Parameter extraction logic
                        let paramStr = content;
                        paramStr = paramStr.split(/{{(.*?)}}/);
                        paramStr = paramStr.filter((a, i) => i % 2 === 1);
                        let uniqueParams = [...new Set(paramStr)].join(", ");
                        setFieldValue(
                          constants.formFields.smsTemplate.parameter,
                          uniqueParams.replace(/<p>(&nbsp;|\s)*<\/p>/g, "")
                        );
                      }}
                      onBlur={handleBlur}
                      error={touched.sms_templates?.[index]?.content && errors.sms_templates?.[index]?.content}
                    />
                  </CCol>
                </CRow>
              ))
            }
          </FieldArray>
          <CRow className="text-center mt-3">
          <CCol lg={12} md={12} sm={12} className="d-flex gap-2 justify-content-center">
              <CButton 
                color="primary" 
                className="px-4 mt-3" 
                type={constants.inputTypes.submit} 
                disabled={smsTemplateLoaded}
                onClick={(e) => {
                }}
              >
                {t(smsTemplateLoaded ? smsTemplate.form.loading_btn_text : smsTemplate.form.submit_btn_text)}
              </CButton>
              <CButton color="secondary" className="px-4 mt-3" type="button" disabled={smsTemplateLoaded} onClick={handleRedirectList}>
                {t(smsTemplate.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>

        </CForm>
      )}
    </Formik>
  );
};

export default SmsTemplateForm;
